<template>
	<article class="column content flexible list-page detail-page subscription iot">
		<!-- <div class="page"> -->
			<header class="centering separated">
				<h1>IoT Subscription</h1>
				<p class="gray-6">Find a plan that fits your IoT needs.</p>
			</header>

			<div v-if="redirectReason">
				<p v-if="redirectReason === 'expired'" class="notice warn subscription-expired">
					Your subscription has expired. Select a new subscription below to continue using this account.
				</p>
			</div>

			<section class="row relative">
				<aside class="overlay updating" v-if="changeSubscriptionStatus.loading || submitting">
					<i class="fa fa-spinner fa-spin"></i>
					Processing&hellip;
				</aside>
				<aside class="overlay updating" v-if="getSubscriptionStatus.loading">
					<i class="fa fa-spinner fa-spin"></i>
					Loading&hellip;
				</aside>
				<section class="flexible">
					<section class="column separated-extra">
						<header class="row rigid baseline">
							<h2 class="gap">Plan Options</h2>
						</header>
						<div class="row plan-controls">
							<div class="gap button-group">
								<label v-for="interval in intervalOptions" v-bind:key="interval" class="button rigid" :class="{active:interval === selection.interval}">
									{{interval | capitalize}}
									<input hidden type="radio" v-model="selection.interval" :disabled="processing" name="billingIntervals" :value="interval" @click="onButtonClick">
								</label>
							</div>

							<a class="bottom rigid" target="_blank" :href="iotPlansUrl">View Plan Details <i class="fas fa-external-link-alt"></i></a>
						</div>
						<plan-placeholder v-if="showPlanLoader" :desc="true"></plan-placeholder>
						<div class="column rigid plan-options">
							<div>
								<label v-for="(plan, index) in availablePlans" v-bind:key="index" class="plan-option bullet flexible" :class="{single: availablePlans.length === 1}">
									<div class="rigid">
										<input type="radio" :disabled="processing" v-model="selection.intervalId" name="plan" :value="plan.intervalId" @click="onButtonClick">
									</div>

									<div class="bullet-content row separated gap">
										<div class="column separated gap">
											<div class="row separated rigid label-wrapper">
												<span class="label trailing-off">{{plan.name}}</span>
												<span class="rigid"><small class="badge rigid" v-if="plan.isCurrent">Current Subscription</small></span>
											</div>

											<span class="row rigid" v-if="plan.description">
												<div class="display-text trailing-off">{{plan.description}}</div>
											</span>
										</div>
										<footer class="row rigid">
											<div class="plan-subscription">
												<div class="plan-rate" :class="{discounted:plan.discounted}">
													<span v-if="plan.customBilling" class="plan-price">Pay-As-You-Go</span>
													<span v-else class="plan-price column">
														<div class="rigid row">{{getBillingRate(plan) | currency(true) || 0}}</div>
													</span>
												</div>
											</div>
										</footer>
									</div>
								</label>
							</div>
						</div>
					</section>
				</section>
				<aside class="rigid column">
					<section>
						<header class="row centering">
							<h2 class="gap">Summary</h2>
							<span v-if="cost.annualDiscount" class="badge badge-success badge-hollow">
								Save {{cost.annualDiscount.percentOff}} yearly
							</span>
						</header>

						<table-placeholder v-if="getSubscriptionStatus.loading"></table-placeholder>

						<div v-else-if="cost.customBilling" class="table">
							<div class="row">
								<div class="column gap">
									<div class="rigid label total">
										Monthly Total
									</div>
								</div>
								<footer class="rigid">
									<strong>Custom</strong>
									<div class="field-assist">Based on usage</div>
								</footer>
							</div>
						</div>

						<div v-else class="table">
							<div class="row">
								<div class="gap label">Subtotal</div>
								<footer class="rigid">
									<span v-if="cost.annualDiscount" class="strikethrough">{{cost.annualDiscount.originalPrice | currency(true)}}</span>
									{{cost.subtotal | currency(true)}}
								</footer>
							</div>
							<div v-if="cost.discount" class="row">
								<div class="gap label">Discount</div>
								<footer class="rigid">-{{cost.discount | currency(true)}}</footer>
							</div>
							<div class="row">
								<div class="column gap">
									<div class="rigid label total">
										<span v-if="isYearly">Yearly</span>
										<span v-else>Monthly</span>
										Total
									</div>
								</div>
								<footer class="rigid">
									<trial-badge v-if="trialExpiry" :expiry="trialExpiry"></trial-badge>

									<strong v-else>
										<span  v-if="cost.total === 0" class="badge badge-success">Free</span>
										<span v-else>{{cost.total | currency(true)}}</span>
									</strong>

									<div v-if="trialExpiry" class="field-assist">{{cost.total | currency(true)}} due {{trialExpiry | date('medium')}}</div>

									<div v-else-if="cost && cost.annualDiscount" class="field-assist">{{cost.annualDiscount.pricePerMonth | currency(true)}} /month</div>

									<!-- <div v-else-if="cost.paymentRequired && selection.expiry" class="field-assist">Until {{selection.expiry | date('medium')}}</div> -->
								</footer>
							</div>
						</div>
					</section>

					<payment-notice v-if="cost.paymentRequired && account.canAddPaymentMethod"
						:trial="isCurrentTrial"
						:on-click="onPaymentClick"
						:returnPath="'/subscriptions/iot'"
						:model="account.paymentMethod"
						:is-dirty="isDirty"
						:loading="!paymentMethodLoaded">
					</payment-notice>

					<footer v-if="account.canAddPaymentMethod" class="column form-options separated-extra">
						<div class="column separated">
							<button class="gap primary" :disabled="submitDisabled" @click="updateSubscription" type="button">
								<span v-if="current">
									Confirm Changes
								</span>
								<span v-else>Subscribe</span>
							</button>
							<button v-if="isDirty" class="gap secondary" @click="resetState" :disabled="processing" type="button">
								Cancel Changes
							</button>
						</div>

						<div v-if="changeSubscriptionStatus.error" class="row rigid">
							<div class="notice error gap">
								<!-- <button type="button" class="close link" @click="removeNotices">×</button> -->
								<div v-if="changeSubscriptionStatus.error.type === 'StripeCardError'">
									{{changeSubscriptionStatus.error.message}}
								</div>
								<div v-else-if="changeSubscriptionStatus.error.type === 'PAYMENT_GATEWAY_ERROR'">
									There was an error processing your transaction.
								</div>
								<div v-else>
									There was an error updating your subscription.
								</div>
							</div>
						</div>

						<div v-if="changeSubscriptionStatus.success" ref="successBox" class="row rigid">
							<div class="notice success gap">
								<!-- <button type="button" class="close link" @click="removeNotices">×</button> -->
								<p>Your subscription has been updated.
									<span v-if="selection.plan.id === 'pln_iot_enterprise'">
										We'll be contacting you shortly.
									</span>
								</p>
							</div>
						</div>
					</footer>
				</aside>
			</section>
		<!-- </div> -->
	</article>
</template>

<script>
import capitalize from '../filters/capitalize';
import currency from '../filters/currency';
import date from '../filters/date';
import { scrollTo } from '../directives/scroll-to';
import PaymentNotice from '../components/PaymentNotice';
import Spinner from '../components/Spinner';
import PlanPlaceholder from '../components/PlanPlaceholder';
import TablePlaceholder from '../components/TablePlaceholder';
import Actions from '../components/Actions';
import { mapGetters, mapState, mapActions } from 'vuex';
import accountService from '@/modules/account-service';
import TrialBadge from '@/components/TrialBadge';
import Vue from 'vue';
import ENV_VARS from '@/modules/env-vars.js';

export default {
	name: 'IoT',
	filters: {
		currency,
		date,
		capitalize
	},
	directives: {
		scrollTo
	},
	components: {
		PaymentNotice,
		Spinner,
		PlanPlaceholder,
		TablePlaceholder,
		TrialBadge
	},
	data() {
		return {
			selection: {
				plan: {},
				id: null,
				interval: null,
				intervalId: null,
				actualInterval: '',
				billingCyclesHash: null,
				hasTrial: false,
				isCurrent: false,
				cost: {},
				changed: false
			},
			isDirty: false,
			previousIntervalId: null,
			submitting: false,
			TRIAL_DAYS: ENV_VARS.TRIAL_DAYS
		};
	},
	computed: {
		...mapGetters([
			'account',
			'accountStatus',
			'PRODUCTS',
			'user'
		]),
		...mapState('subscription', {
			intervalOptions: 'intervalOptions',
			storedSelection: 'selection',
			current: 'currentSubscription',
			plans: 'plans',
			getSubscriptionStatus: 'getSubscriptionStatus',
			changeSubscriptionStatus: 'changeSubscriptionStatus'
		}),
		...mapState('auth', [
			'refreshToken'
		]),
		iotPlansUrl(){
			return 'https://' + (this.$root.isDev ? 'dev-' : '') + 'www.initialstate.com/#pricing';
		},
		isYearly() {
			return this.selection.interval === 'yearly';
		},
		cost() {
			return this.getCost(this.selection && this.selection.plan);
		},
		availablePlans() {
			let vm = this;
			// display as yearly nees to filter by free otherwise use the selected interval to filter
			let filterInterval = vm.selection.interval || vm.intervalOptions[0];

			vm.limitedOfferAvailable = false;
			let plansCopy = this.deepCopy(vm.plans);
			let plans = plansCopy.filter(plan => {

				return plan.billingCycles.find(cycle => {
					// flag limited offer to hide show disclaimer
					if (!vm.limitedOfferAvailable) {
						vm.limitedOfferAvailable = cycle.limitedOffer;
					}

					if (cycle.interval === filterInterval || (cycle.interval === 'free' && filterInterval === 'monthly')) {
						plan.intervalId = cycle.intervalId;
						plan.hasTrial = cycle.hasTrial;
						plan.interval = cycle.interval;
						plan.isCurrent = vm.current && vm.current.plan.intervalId === cycle.intervalId;
						return true;
					}
				});
			});
			return plans;
		},
		trialEligible() {
			return this.selection.actualInterval !== 'free' && this.current && this.current.trialExpiry;
		},
		isCurrentTrial() {
			return this.trialExpiry !== null;
		},
		trialExpiry() {
			if (this.cost.total === 0){
				return null;
			}
			const now = new Date();
			const startDate = new Date(now);
			return (this.current && this.current.trialExpiry) ? new Date(this.current.trialExpiry) : this.selection.hasTrial ? new Date(startDate.setDate(startDate.getDate() + this.TRIAL_DAYS)) : null;
		},
		submitDisabled() {
			return this.processing || (!this.isDirty && this.current) || (this.cost.paymentRequired && ((!this.account.paymentMethod && !this.selection.hasTrial)));
		},
		processing() {
			return this.getSubscriptionStatus.loading || this.changeSubscriptionStatus.loading || this.submitting;
		},
		showPlanLoader() {
			return this.getSubscriptionStatus.loading && this.availablePlans.length === 0;
		},
		paymentMethodLoaded() {
			return this.account.paymentMethod !== undefined;
		},
		redirectReason() {
			return this.$route.query && this.$route.query.r;
		},
		credit() {
			return this.account.balanceCents;
		},
		paymentRequired() {
			return (this.credit + this.total) > 0;
		}
	},
	methods: {
		...mapActions('subscription', {
			clearStoredSelection: 'CLEAR_SELECTION',
		}),
		selectPlan(plan){
			if (!plan) {
				return;
			}
			let planObj = {
				plan: plan,
				id: plan.id,
				interval: this.getInterval(plan),
				actualInterval: this.getActualInterval(plan),
				intervalId: plan.intervalId || plan.id + '_' + plan.interval,
				billingCyclesHash: plan && plan.billingCyclesHash,
				hasTrial: plan.hasTrial || false,
				isCurrent: plan.isCurrent
			};

			this.setSelection(planObj);
		},
		selectCurrent(current){
			let currentPlan = this.getPlanByIntervalId(current.plan.intervalId) || this.getPlanById(current.plan.id);
			let currentObj = {
				plan: currentPlan,
				id: current.plan.id,
				interval: this.getInterval(current.plan),
				actualInterval: this.getActualInterval(current.plan),
				intervalId: current.plan.intervalId,
				billingCyclesHash: currentPlan.billingCyclesHash,
				isCurrent: current.plan.isCurrent,
				trialExpiry: current.trialExpiry
			};

			this.setSelection(currentObj, true);
		},
		onPaymentClick(){
			this.$store.dispatch('subscription/STORE_SELECTION', this.selection);
		},
		setSelection(selection, isCurrent) {
			Vue.set(this, 'selection', selection);
		},
		deepCopy(obj) {
			return JSON.parse(JSON.stringify(obj));
		},
		getCost(plan) {
			let discount = 0, //set to zero until coupons are functional
				subtotal = this.getBillingRate(plan), //revisit add-on pricing calc
				total = subtotal - discount,
				annualDiscount = this.getAnnualDiscount(plan, total),
				paymentRequired = ((this.credit || 0) + total) > 0 || plan.customBilling,
				customBilling = plan.customBilling;

			return {
				paymentRequired,
				annualDiscount,
				discount,
				subtotal,
				total,
				customBilling
			};
		},
		getPlanByIntervalId(intervalId) {
			return this.deepCopy(this.plans).find(plan => {
				return plan.intervalId === intervalId;
			});
		},
		getPlanById(id) {
			return this.deepCopy(this.plans).find(plan => {
				return plan.id === id;
			});
		},
		getInterval(plan) {
			return (!plan.interval || plan.interval === 'free') ? 'monthly' : plan.interval;
		},
		getActualInterval(plan) {
			return plan.interval;
		},
		onButtonClick() {
			this.isDirty = true;
			this.removeNotices();
		},
		getBillingRate(plan) {
			let interval = plan.interval || this.selection.actualInterval;
			return plan && plan.billingCyclesHash && plan.billingCyclesHash[interval] && plan.billingCyclesHash[interval].amount || 0;
		},
		getAnnualDiscount(plan, total) {
			if (!this.isYearly || !total ) {
				return false;
			}

			let monthlyAmount = plan.billingCyclesHash && plan.billingCyclesHash.monthly && plan.billingCyclesHash.monthly.displayAmount || 0;

			if (!monthlyAmount) {
				return false;
			}

			let decrease = monthlyAmount - (total / 12);
			let percentage = decrease / (monthlyAmount || 1) * 100;

			if (percentage > 0) {
				return {
					percentOff: Math.floor(percentage) + '%',
					originalPrice: monthlyAmount * 12,
					pricePerMonth: total / 12
				};
			}
		},
		updateSubscription() {
			const vm = this;

			if (vm.current) {
				const currentPlan = vm.current && vm.current.plan || {};
				// const currentAddOnNames = this.getSelectedAddOns(this.current.plan.addOns);
				const patchObj = {
					subId: vm.current.id,
					// interval: vm.selection.actualInterval
				}; // only send props that have changed


					//always send interval
				patchObj.interval = vm.selection.actualInterval;

				if (vm.selection.id !== currentPlan.id) {
					patchObj.id = vm.selection.id;
				}


				vm.$store.dispatch('subscription/CHANGE_SUBSCRIPTION_REQUEST', patchObj).then(() => {
					vm.isDirty = false;
					return vm.$store.dispatch('auth/SET_REFRESH_TOKEN', vm.refreshToken);
				}).catch(() => {});

			} else {
				let subObj = {};

				subObj = {
					interval: vm.selection.actualInterval,
					id: vm.selection.id,
					// quantity: vm.selection.quantity,
					// addOns: addOnNames
				};

				vm.$store.dispatch('subscription/ADD_SUBSCRIPTION_REQUEST', subObj).then(() => {
					vm.isDirty = false;
					return vm.$store.dispatch('auth/SET_REFRESH_TOKEN', vm.refreshToken);
				}).catch((error) => {

				});
			}
		},
		leavePlan() {
			this.resetState();
			this.$router.push('/subscriptions');
		},
		restoreSelection() {
			// leave page and come back
			this.isDirty = !!this.storedSelection;
			this.setSelection(this.deepCopy(this.storedSelection));
		},
		resetState() {
			// reset all the things
			let vm = this;
			vm.previousIntervalId = vm.current ? vm.current.plan.intervalId : null;
			vm.$store.dispatch('subscription/RESET_PLANS_STATE');
			vm.$store.dispatch('subscription/RESET_CHANGE_SUBSCRIPTION_STATUS');

			// has a current sub
			if (vm.current) {
				    this.selectCurrent(vm.current);
				// no sub
			} else {
				// set back to first interval option
				vm.selection.interval = vm.intervalOptions[0];
			}

			vm.isDirty = false;
			vm.limitedOfferAvailable = false;
		},
		resetCouponState() {
			this.couponDetails = null;
			this.couponDetailsError = null;
			this.couponCodeModel = null;
		},
		restoreSubscriptionState() {
			// load initial plan options
			this.$store.dispatch('subscription/RESET_PLANS_STATE');
			this.resetCouponState();
		},
		removeNotices() {
			// remove notices to clean up view
			this.$store.dispatch('subscription/RESET_CHANGE_SUBSCRIPTION_STATUS');
			this.showRemoveNotice = false;
		}
	},
	watch: {
		'selection.interval': function(interval, was) {
			if (interval && was) { // user previous intervalId to select previous option when switching interval
				let key = this.previousIntervalId ? 'intervalId' : 'interval';
				let value = this.previousIntervalId || interval;

				let plan = this.availablePlans.find(plan => plan[key] === value) || this.availablePlans[0];
				this.selectPlan(plan);
			}
		},
		'selection.intervalId': function(intervalId, was) {
			if (intervalId && was) {
				let plan = this.availablePlans.find(plan => plan.intervalId === intervalId) || this.availablePlans[0];
				this.selectPlan(plan);
			}
			this.previousIntervalId = was;
		},
		plans: function(plans, old) {
			let defaultPlan = this.availablePlans.length && this.availablePlans[0];
			if (plans.length) {
				if (this.storedSelection && !this.couponDetails) {
					// coming back from payment method
					this.restoreSelection();
				} else if (defaultPlan && (!this.current)) {
					// plans list is replaced with a single prepaid plan
					this.selectPlan(defaultPlan);
				}
			}
		},
		current(current, old) {
			if (current) {
				if (!this.storedSelection) {
					this.selectCurrent(current);
				}
			}
		}
	},
	created() {
		this.$store.dispatch('subscription/GET_SUBSCRIPTION_REQUEST',  { product: 'iot'});
	},
	beforeDestroy() {
		this.$store.dispatch('subscription/TEAR_DOWN');
	}
};
</script>

<style scoped>
	.overlay.updating {
		width: 200px;
	}
</style>



// WEBPACK FOOTER //
// ./src/views/IoT.vue